import styled from 'styled-components';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageUploader from '../ImageUploader/ImageUploader';
import ColorPicker from '../ColorPicker/ColorPicker';
import CustomFontUploader from '../CustomFontUploader/CustomFontUploader';
import { ReactComponent as FlagDe } from '../../assets/images/flags/de.svg';
import { ReactComponent as FlagGb } from '../../assets/images/flags/gb.svg';
import { ITrustpageEntry } from '../../shared/interfaces/ITrustpageEntry';
import { IMonitoring } from '../../shared/interfaces/IMonitoring';
import PlatformService from '../../services/PlatformService';
import { IPlatform } from '../../shared/interfaces/IPlatform';

interface Props {
  trustpage: ITrustpageEntry;
  edit: boolean;
  onChange: (trustpage: ITrustpageEntry) => void;
  onMonitoringChange: (monitorings: string[]) => void;
  selectedMonitorings: string[];
  configuredMonitorings: IMonitoring[];
}

const ListContainer = styled.div`
  display: flex;
  padding: ${(props) => props.theme.spacing(2)} ${(props) => props.theme.spacing(1)}
    ${(props) => props.theme.spacing(1)} ${(props) => props.theme.spacing(1)};

  :not(:last-child) {
    border-bottom: 1px solid ${(props) => props.theme.palette.primary.main};
  }
`;

const TrustpageItem: FC<Props> = ({
  trustpage,
  onChange,
  onMonitoringChange,
  selectedMonitorings,
  configuredMonitorings,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [trustpageSettings, setTrustpageSettings] = useState<ITrustpageEntry>(trustpage);
  const {
    companyName,
    companyWebsiteUrl,
    contactEmail,
    primaryColor,
    secondaryColor,
    lang,
    imprintLink,
    privacyPolicyLink,
    aboutCompanyText,
  } = trustpageSettings;
  const [currentMonitorings, setCurrentMonitorings] = useState<string[]>(selectedMonitorings);
  const [loading, setLoading] = useState<boolean>(false);
  const [platforms, setPlatforms] = useState<IPlatform[]>([]);
  const [currentPlatformLabels, setCurrentPlatformLabels] = useState<string[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    const platformArray: string[] = [];

    const getPlatforms = async () => {
      setLoading(true);
      try {
        const platforms = await PlatformService.getPlatforms(setLoading, controller.signal);
        const platformIDs = configuredMonitorings.map((monitoring) => monitoring.platformId);

        platformIDs.forEach((platformID) => {
          const platform = PlatformService.getPlatformName(platformID, platforms);
          platformArray.push(platform === 'Unknown platform' ? 'google' : platform);
        });

        if (!platforms || platforms.length === 0) return;

        setPlatforms(platforms);
        setCurrentPlatformLabels(platformArray);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    (async () => await getPlatforms())();

    return () => {
      controller.abort();
    };
  }, [configuredMonitorings]);

  const handleLogoChange = useCallback(
    (logoFile: File) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const updatedSettings = {
          ...trustpageSettings,
          companyLogo: event.target?.result?.toString() || '',
        };
        setTrustpageSettings(updatedSettings);
      };
      reader.readAsDataURL(logoFile);
    },
    [trustpageSettings]
  );

  const handleColorChange = useCallback(
    (colorType: 'primaryColor' | 'secondaryColor') => (colorValue: string) => {
      const updatedSettings = {
        ...trustpageSettings,
        [colorType]: colorValue,
      };
      setTrustpageSettings(updatedSettings);
    },
    [trustpageSettings]
  );

  const handleFontChange = useCallback(
    (fontFile: File) => {
      const reader = new FileReader();
      const extension = fontFile.name.split('.').pop()?.toLowerCase() || '';
      reader.onload = (event) => {
        const updatedSettings = {
          ...trustpageSettings,
          customFont: event.target?.result?.toString() || '',
          customFontType: extension,
        };
        setTrustpageSettings(updatedSettings);
      };
      reader.readAsDataURL(fontFile);
    },
    [trustpageSettings]
  );

  useEffect(() => {
    onMonitoringChange(currentMonitorings);
  }, [onMonitoringChange, currentMonitorings]);

  useEffect(() => {
    onChange(trustpageSettings);
  }, [onChange, trustpageSettings]);

  const defineType = (monitoringId: string) => {
    const monitoring = configuredMonitorings.find((monitoring) => monitoring.id === monitoringId);
    return monitoring?.type || 'MONITORING';
  };

  const handleMonitoringChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    const monitoringsArray = typeof value === 'string' ? value.split(',') : value;

    setCurrentMonitorings(monitoringsArray);

    const updatedSettings = {
      ...trustpageSettings,
      monitorings: monitoringsArray.map((monitoringId) => ({
        id: monitoringId,
        type: defineType(monitoringId),
      })),
    };

    setTrustpageSettings(updatedSettings);
  };

  const handleLangChange = (event: React.MouseEvent<HTMLElement>, newLang: string) => {
    const updatedSettings = {
      ...trustpageSettings,
      lang: newLang,
    };
    setTrustpageSettings(updatedSettings);
  };

  const handleInputChange =
    (field: keyof ITrustpageEntry) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const updatedSettings = {
        ...trustpageSettings,
        [field]: event.target.value,
      };
      setTrustpageSettings(updatedSettings);
    };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        m: theme.spacing(2),
        overflow: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography>{t('logo_label')}</Typography>
        <ImageUploader onChange={handleLogoChange} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: theme.spacing(2),
          justifyContent: 'space-between',
        }}
      >
        <Typography>{t('primary_color_label')}</Typography>
        <ColorPicker
          defaultValue={primaryColor}
          onChange={handleColorChange('primaryColor')}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: theme.spacing(2),
          justifyContent: 'space-between',
        }}
      >
        <Typography>{t('secondary_color_label')}</Typography>
        <ColorPicker
          defaultValue={secondaryColor}
          onChange={handleColorChange('secondaryColor')}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: theme.spacing(2),
          justifyContent: 'space-between',
        }}
      >
        <Typography>{t('font')}</Typography>
        <CustomFontUploader onChange={handleFontChange} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: theme.spacing(2),
        }}
      >
        <Typography>{t('language')}</Typography>
        <ToggleButtonGroup
          color="primary"
          size="small"
          value={lang}
          exclusive
          onChange={handleLangChange}
          sx={{
            '& .Mui-selected.MuiToggleButtonGroup-grouped': {
              border: `1px solid ${theme.palette.primary.main}`,
            },
          }}
        >
          <ToggleButton value="de">
            <FlagDe
              width="32px"
              height="100%"
            />
          </ToggleButton>
          <ToggleButton value="gb">
            <FlagGb
              width="32px"
              height="100%"
            />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <TextField
        fullWidth
        size="small"
        defaultValue={companyName}
        label={t('company_name')}
        sx={{ mb: theme.spacing(1), mt: theme.spacing(2) }}
        onChange={handleInputChange('companyName')}
      />
      <TextField
        fullWidth
        size="small"
        defaultValue={companyWebsiteUrl}
        label={t('website')}
        sx={{ mb: theme.spacing(1), mt: theme.spacing(2) }}
        onChange={handleInputChange('companyWebsiteUrl')}
      />
      <TextField
        fullWidth
        size="small"
        rows={4}
        multiline
        defaultValue={aboutCompanyText}
        label={t('about_company_placeholder')}
        sx={{ mb: theme.spacing(1), mt: theme.spacing(2) }}
        onChange={handleInputChange('aboutCompanyText')}
      />
      <TextField
        fullWidth
        size="small"
        defaultValue={contactEmail}
        label={t('contact_email_label')}
        sx={{ mb: theme.spacing(1), mt: theme.spacing(2) }}
        onChange={handleInputChange('contactEmail')}
      />
      <TextField
        fullWidth
        size="small"
        defaultValue={imprintLink}
        label={t('imprint')}
        sx={{ mb: theme.spacing(1), mt: theme.spacing(2) }}
        onChange={handleInputChange('imprintLink')}
      />
      <TextField
        fullWidth
        size="small"
        defaultValue={privacyPolicyLink}
        label={t('privacy_policy_link_label')}
        sx={{ mb: theme.spacing(1), mt: theme.spacing(2) }}
        onChange={handleInputChange('privacyPolicyLink')}
      />
      <FormControl
        fullWidth
        sx={{ mb: theme.spacing(1), mt: theme.spacing(2) }}
      >
        <InputLabel id="monitoring">{t('monitoring')}</InputLabel>
        <Select
          labelId="monitoring"
          label={t('monitoring')}
          size="small"
          multiple
          sx={{ minWidth: 200, paddingBottom: theme.spacing(1) }}
          value={currentMonitorings}
          onChange={handleMonitoringChange}
        >
          {configuredMonitorings.map((monitoring, index) => (
            <MenuItem
              key={monitoring.id}
              value={monitoring.id}
            >
              {`${monitoring.label}(${currentPlatformLabels[index]})`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default TrustpageItem;
